import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IconButton,
  StyledHeader,
  StyledVersionDialog,
  UserGuideContainer,
  VersionContainer,
  VersionDialogTitle,
} from "./Header.styled";

import { AppConfigContext } from "../../../providers/AppConfigProvider";
import { AppGlobalDataContext } from "../../../providers/AppGlobalDataProvider";
import logo from "../../../images/app-logo.png";
import InformationIcon from "../../svg-icons/InformationIcon";
import PowerIcon from "../../svg-icons/PowerIcon";
import Tooltip from "../Tooltip/Tooltip";
import HelpIcon from "../../svg-icons/HelpIcon";
import ExportDialog from "../ExportDialog/ExportDialog";

type UserGuideManifest = { pages: number };

export const Header: React.FC = () => {
  const config = useContext(AppConfigContext);
  const { qtContext, patient, capturedSnapshots, translations, locale } =
    useContext(AppGlobalDataContext);

  const [openVersionDialog, setOpenVersionDialog] = useState(false);
  const [isExitPromptActive, setIsExitPromptActive] = useState(false);

  const [openUserGuide, setOpenUserGuide] = useState(false);
  const [pages, setPages] = useState<number>(0);
  const [isGuideLoading, setIsGuideLoading] = useState(false);

  // Important: this is the folder we will actually use after fallback
  const [activeBasePath, setActiveBasePath] = useState<string>("");

  const handleOnAppClose = useCallback(() => {
    if (capturedSnapshots.length && capturedSnapshots.some((el) => !el.saved)) {
      setIsExitPromptActive(true);
    } else {
      if (qtContext) qtContext.quit();
      else window?.close();
    }
  }, [qtContext, capturedSnapshots]);

  const requestedLocale = useMemo(() => {
    return locale ?? "en_US";
  }, [locale]);

  const openGuide = useCallback(() => {
    setOpenUserGuide(true);
  }, []);

  const closeGuide = useCallback(() => {
    setOpenUserGuide(false);
    setPages(0);
  }, []);

  useEffect(() => {
    if (!openUserGuide) return;

    let cancelled = false;

    const loadManifestFor = async (loc: string) => {
      const basePath = `/user-guides/png/${loc}`;
      const manifestUrl = `${basePath}/manifest.json`;

      const res = await fetch(manifestUrl, { cache: "no-cache" });
      if (!res.ok) throw new Error(`Manifest request failed: ${res.status}`);

      const data = (await res.json()) as UserGuideManifest;

      if (!cancelled) {
        setPages(Number.isFinite(data.pages) ? data.pages : 0);
        setActiveBasePath(basePath);
      }
    };

    const run = async () => {
      setIsGuideLoading(true);
      setPages(0);

      try {
        // 1) Try requested locale
        await loadManifestFor(requestedLocale);
      } catch (e) {
        console.error(
          `Could not load user guide for locale ${requestedLocale}, falling back to en_US.`,
          e
        );

        try {
          // 2) Fallback
          await loadManifestFor("en_US");
        } catch (e2) {
          console.error("[HELP] Fallback en_US failed too.", e2);
          if (!cancelled) {
            setPages(0);
            setActiveBasePath("");
          }
        }
      } finally {
        if (!cancelled) setIsGuideLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [openUserGuide, requestedLocale]);

  const getPageSrc = useCallback(
    (pageIndex: number) => {
      const pageNumber = String(pageIndex + 1).padStart(2, "0");
      return `${activeBasePath}/page-${pageNumber}.png`;
    },
    [activeBasePath]
  );

  return (
    <StyledHeader>
      <img src={logo} alt="App logo" />
      <span>{patient?.name}</span>

      <Tooltip title={translations["HELP"]}>
        <IconButton onClick={openGuide} data-html2canvas-ignore>
          <HelpIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={translations["ABOUT"]}>
        <IconButton
          onClick={() => setOpenVersionDialog(true)}
          data-html2canvas-ignore
        >
          <InformationIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={translations["CLOSE"]}>
        <IconButton onClick={handleOnAppClose} data-html2canvas-ignore>
          <PowerIcon />
        </IconButton>
      </Tooltip>

      {/* About dialog */}
      <StyledVersionDialog
        open={openVersionDialog}
        onClose={() => setOpenVersionDialog(false)}
      >
        <VersionContainer>
          <VersionDialogTitle>{translations["ABOUT"]}</VersionDialogTitle>
          <img src={logo} alt="App logo" />
          <span>
            {translations["PRODUCT_VERSION"]}: {config?.buildVersion}
          </span>
          <p>
            &copy;2024, Dental Imaging Technologies Corporation. All right
            reserved.
          </p>
        </VersionContainer>
      </StyledVersionDialog>

      {/* User Guide dialog (PNG) */}
      <StyledVersionDialog open={openUserGuide} onClose={closeGuide}>
        <UserGuideContainer>
          <VersionDialogTitle>
            Oral Compare {translations["USER_GUIDE"]}
          </VersionDialogTitle>
          <div className="pdf-viewer">
            {isGuideLoading && <p>Loading…</p>}
            {!isGuideLoading && pages === 0 && (
              <p>Unable to load the user guide.</p>
            )}
            {!isGuideLoading &&
              pages > 0 &&
              Array.from({ length: pages }, (_, i) => (
                <img
                  key={`page_${i + 1}`}
                  src={getPageSrc(i)}
                  alt={`User guide page ${i + 1}`}
                  loading="lazy"
                  style={{ width: "100%", display: "block", marginBottom: 12 }}
                  onError={(e) => {
                    console.error(
                      "[HELP] Failed to load page image:",
                      (e.target as HTMLImageElement).src
                    );
                  }}
                />
              ))}
          </div>
          <button onClick={closeGuide} className="close-button">
            {translations["CLOSE"]}
          </button>
        </UserGuideContainer>
      </StyledVersionDialog>
      {isExitPromptActive && (
        <ExportDialog onClose={() => setIsExitPromptActive(false)} />
      )}
    </StyledHeader>
  );
};
